/**
 * @fileoverview Session Timeout Hook
 * @description Auto-logout after inactivity. Extended to 60 minutes for long work sessions.
 * Shows warning at 55 min with option to extend.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes
const WARNING_MS = 55 * 60 * 1000; // Warning at 55 minutes

export function useSessionTimeout() {
  const { user, signOut } = useAuthContext();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const warningRef = useRef<ReturnType<typeof setTimeout>>();
  const lastResetRef = useRef(Date.now());

  const resetTimer = useCallback(() => {
    if (!user) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    lastResetRef.current = Date.now();

    warningRef.current = setTimeout(() => {
      toast.warning('Session expiring soon', {
        description: 'You will be logged out in 5 minutes due to inactivity. Move your mouse or press any key to stay logged in.',
        duration: 30000,
        action: {
          label: 'Stay Logged In',
          onClick: () => {
            resetTimer();
            toast.success('Session extended', { duration: 3000 });
          },
        },
      });
    }, WARNING_MS);

    timeoutRef.current = setTimeout(() => {
      toast.error('Session expired', {
        description: 'You have been logged out due to inactivity.',
      });
      signOut();
    }, TIMEOUT_MS);
  }, [user, signOut]);

  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    // Throttle to avoid excessive timer resets (every 2 minutes)
    const throttledReset = () => {
      if (Date.now() - lastResetRef.current > 120000) {
        resetTimer();
      }
    };

    events.forEach(e => window.addEventListener(e, throttledReset, { passive: true }));
    resetTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, throttledReset));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [user, resetTimer]);
}
